/**
 * Anchored reply composer. A section affordance carries its anchor in data-* attributes, and
 * the composer sends an ordinary PM with a profile_ref attachment through POST /v1/message.
 * Ids stay strings, because they exceed Number.MAX_SAFE_INTEGER.
 */

function readAnchorFromButton(btn) {
    return {
        kind: btn.dataset.anchorKind || '',
        label: btn.dataset.anchorLabel || '',
        excerpt: btn.dataset.anchorExcerpt || '',
        entityId: btn.dataset.anchorEntityId || '',
        lang: btn.dataset.anchorLang || '',
        thumb: btn.dataset.anchorThumb || '',
    };
}

document.addEventListener('click', (e) => {
    const btn = e.target.closest('.js-anchor-reply');
    if (!btn) {
        return;
    }
    e.preventDefault();
    window.dispatchEvent(new CustomEvent('anchor-reply:open', { detail: { ...readAnchorFromButton(btn), trigger: btn } }));
});

/**
 * POST an anchored private message. The anchored content is always the recipient's own
 * profile, so the anchor owner is the recipient.
 *
 * @returns {Promise<Response>}
 */
function postAnchoredMessage({ recipientId, kind, entityId, lang, excerpt, body, tmpId }) {
    const fd = new FormData();
    fd.append('tmp_id', tmpId);
    fd.append('target_id', recipientId);
    fd.append('message', body);
    fd.append('attachment_type', 'profile_ref');
    fd.append('anchor_kind', kind);
    fd.append('anchor_owner_uid', recipientId);
    if (entityId) {
        fd.append('anchor_entity_id', entityId);
    }
    if (lang) {
        fd.append('anchor_lang', lang);
    }
    // Only a text section stores its excerpt. The server refuses the send when that text changed.
    if (excerpt && !entityId && !lang) {
        fd.append('anchor_excerpt', excerpt);
    }

    return window.ApiClient.request('/message', { method: 'POST', body: fd });
}

/**
 * Extract a human-readable string from the API error envelope
 * ({ error: { message, errors: { field: { message } } } }). Only ever returns a string so
 * x-text never renders "[object Object]".
 */
async function anchoredSendErrorMessage(res, fallback, changedText) {
    try {
        const data = await res.json();
        const err = data && data.error;
        const fieldErrors = err && err.errors;
        // The composer sends only anchors the page rendered, so an anchor error means the section changed.
        if (fieldErrors && typeof fieldErrors === 'object' && changedText
            && Object.keys(fieldErrors).some((field) => field.startsWith('anchor_'))) {
            return changedText;
        }
        if (fieldErrors && typeof fieldErrors === 'object') {
            const first = Object.values(fieldErrors)[0];
            if (first && typeof first.message === 'string' && first.message) {
                return first.message;
            }
        }
        if (err && typeof err.message === 'string' && err.message) {
            return err.message;
        }
        if (data && typeof data.message === 'string' && data.message) {
            return data.message;
        }
    } catch {
        // The body is not JSON, so the generic message applies.
    }
    return fallback;
}

function newTmpId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID().replace(/-/g, '');
    }
    return (Date.now().toString(36) + Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)).slice(0, 32);
}

function anchorReplyComposer() {
    return {
        open: false,
        sending: false,
        sent: false,
        error: '',
        body: '',
        anchor: { kind: '', label: '', excerpt: '', entityId: '', lang: '', thumb: '' },
        recipientId: '',
        headerTitle: '',
        errorText: '',
        changedText: '',
        sentText: '',
        threadUrl: '',
        tmpId: '',
        tmpIdBody: null,
        trigger: null,
        minBodyChars: 1,

        init() {
            const data = this.$el.dataset;
            this.recipientId = String(data.recipientId || '');
            this.headerTitle = data.headerTitle || '';
            this.errorText = data.errorText || '';
            this.changedText = data.changedText || '';
            this.sentText = data.sentText || '';
            this.threadUrl = data.threadUrl || '';
            this.minBodyChars = Number(data.minBodyChars) || 1;
        },

        // Code points, matching the mb_strlen() count of MessageAnchorService::assertBody().
        get bodyLength() {
            return [...this.body.trim()].length;
        },

        get canSend() {
            return this.bodyLength >= this.minBodyChars;
        },

        get tooShort() {
            return this.bodyLength > 0 && !this.canSend;
        },

        openWith(detail) {
            this.trigger = detail.trigger || null;
            // A send in flight keeps its draft, so its response always lands in this composer.
            if (this.sending) {
                this.open = true;
                return;
            }
            const sameAnchor = !this.sent
                && this.anchor.kind === (detail.kind || '')
                && this.anchor.entityId === (detail.entityId || '')
                && this.anchor.lang === (detail.lang || '');
            this.anchor = {
                kind: detail.kind || '',
                label: detail.label || '',
                excerpt: detail.excerpt || '',
                entityId: detail.entityId || '',
                lang: detail.lang || '',
                thumb: detail.thumb || '',
            };
            // An unsent draft follows the viewer to another section. A sent one does not.
            if (this.sent) {
                this.body = '';
            }
            if (!sameAnchor) {
                // One key per draft and anchor, so a retry after a lost response cannot send twice.
                this.tmpId = newTmpId();
                this.tmpIdBody = null;
            }
            this.error = '';
            this.sent = false;
            this.open = true;
            this.$nextTick(() => {
                if (window.LucideIcons) {
                    window.LucideIcons.initialize(this.$el);
                }
                if (this.$refs.textarea) {
                    this.$refs.textarea.focus();
                }
            });
        },

        close() {
            this.open = false;
            const trigger = this.trigger;
            if (trigger) {
                this.$nextTick(() => trigger.focus());
            }
        },

        async send() {
            const body = this.body.trim();
            if (!this.canSend || this.sending) {
                return;
            }
            // A key already sent with other text would return that message as a duplicate.
            if (this.tmpIdBody !== null && this.tmpIdBody !== body) {
                this.tmpId = newTmpId();
            }
            this.tmpIdBody = body;
            this.sending = true;
            this.error = '';
            try {
                const res = await postAnchoredMessage({
                    recipientId: this.recipientId,
                    kind: this.anchor.kind,
                    entityId: this.anchor.entityId,
                    lang: this.anchor.lang,
                    excerpt: this.anchor.excerpt,
                    body,
                    tmpId: this.tmpId,
                });
                const gate = window.PhoneGateRefusal;
                if (gate && await gate.followApi(res)) {
                    this.sending = false;
                    return;
                }
                if (!res.ok) {
                    this.error = await anchoredSendErrorMessage(res, this.errorText, this.changedText);
                    this.sending = false;
                    return;
                }

                this.sent = true;
                this.sending = false;
                this.$nextTick(() => {
                    if (window.LucideIcons) {
                        window.LucideIcons.initialize(this.$el);
                    }
                    if (this.$refs.viewConversation) {
                        this.$refs.viewConversation.focus();
                    }
                });
            } catch {
                this.error = this.errorText;
                this.sending = false;
            }
        },
    };
}

document.addEventListener('alpine:init', () => {
    if (window.Alpine) {
        window.Alpine.data('anchorReplyComposer', anchorReplyComposer);
    }
});
